<template>
  <div v-if="value" v-bsl="value"></div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';
import moment from 'moment';

import TeacherSvg from '@/assets/images/onboarding/classlist.svg';
import HomeworksSymbolsPng from '@/assets/images/onboarding/homeworks.png';
import ArrowTopImg from '@/assets/images/onboarding/arrowtop.svg';
import ArrowBottomImg from '@/assets/images/onboarding/arrowbottom.svg';

export default {
  name: 'HomeworksDesktop',
  props: {
    value: {
      required: true,
    },
    isEmpty: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    ...mapState('students', ['student']),

    teacherImage() {
      return TeacherSvg;
    },

    homeworksSymbolsImage() {
      return HomeworksSymbolsPng;
    },

    arrowTopImage() {
      return ArrowTopImg;
    },

    arrowBottomImage() {
      return ArrowBottomImg;
    },

    onboardingStepOneText() {
      return `
        <div class="flex flex-col justify-center items-center">
          <img style="max-width: 155px" src="${this.teacherImage}"/>
          <h3 class="text-center">${this.$t('onboarding.homeworks_step1.title')}</h3>
          <p class="text-center py-4 px-6">${this.$t('onboarding.homeworks_step1.content')}</p>
          <p class="text-center pb-4 px-6">
            <span>${this.$t('onboarding.homeworks_step1.content2')}</span><span class="text-black">"</span><span class="font-bold text-blue-zoom">${this.$t('onboarding.homeworks_step1.content3')}</span><span class="text-black">"</span><span>${this.$t('onboarding.homeworks_step1.content4')}</span>
          </p>
        </div>`;
    },

    onboardingStepTwoText() {
      return `
        <div class="flex flex-col justify-center items-center">
          <h3 class="text-center">${this.$t('onboarding.homeworks_step2.title')}</h3>
          <p class="text-center py-4">${this.$t('onboarding.homeworks_step2.content')}</p>
          <div>
            <img src="${this.homeworksSymbolsImage}"/>
          </div>
        </div>`;
    },
  },
  methods: {
    ...mapMutations('layout', ['showClassList', 'hideClassList']),
    ...mapActions('onboarding', ['setOnboardingProperty']),

    closeOnboardingTag() {
    },

    nextOnboardingTag() {
    },

    prevOnboardingTag() {
    },

    acceptOnboardingTag() {
    },

    addProgressDots(tour) {
      const currentStepElement = tour.currentStep.el;
      const header = currentStepElement.querySelector('.shepherd-header');
      const progress = document.createElement('span');
      progress.style['margin-right'] = '224px';
      progress.innerText = `${tour.steps.indexOf(tour.currentStep) + 1}/${
        tour.steps.length
      }`;
      header.insertBefore(
        progress,
        currentStepElement.querySelector('.shepherd-cancel-icon'),
      );
    },

    addArrow(tour, direction) {
      const currentStepElement = tour.currentStep.el;
      const header = currentStepElement.querySelector('.shepherd-header');
      const progress = document.createElement('img');
      if (direction === 'top') {
        progress.classList.add('stepherd-custom-arrow--top');
        progress.src = `${this.arrowTopImage}`;
      }
      if (direction === 'left') {
        progress.classList.add('stepherd-custom-arrow--left');
        progress.src = `${this.arrowBottomImage}`;
      }
      header.insertBefore(
        progress,
        currentStepElement.querySelector('.shepherd-cancel-icon'),
      );
    },

    getDateWithYearlyOffset(years) {
      return moment().add(years, 'y').toDate();
    },

    setTourCookie() {
      if (this.student?.id) {
        this.setOnboardingProperty({ deviceType: 'desktop', property: 'homework', value: true });
      }
    },

    startOnboarding() {
      this.$nextTick(() => {
        const vm = this;
        const tour = this.$shepherd({
          useModalOverlay: true,
          defaultStepOptions: {
            cancelIcon: {
              enabled: true,
            },
          },
        });

        tour.addStep({
          text: vm.onboardingStepOneText,
          beforeShowPromise() {
            return new Promise((resolve) => {
              setTimeout(() => {
                window.scrollTo(0, 0);
                resolve();
              }, 500);
            });
          },
          attachTo: {
            element: this.isEmpty ? null : '.classList',
            on: this.isEmpty ? null : 'top-end',
          },
          modalOverlayOpeningRadius: 12,
          popperOptions: {
            placement: 'top-end',
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 20],
                },
              },
            ],
          },
          buttons: [
            {
              text: this.$t('onboarding.homeworks_step1.btn'),
              action() {
                vm.nextOnboardingTag();
                return this.next();
              },
            },
          ],
          when: {
            show() {
              if (!vm.isEmpty) {
                vm.addArrow(this.tour, 'top');
              }
              vm.addProgressDots(this.tour);
            },
            cancel() {
              vm.closeOnboardingTag();
              vm.$emit('input', !vm.value);
              vm.showClassList();
              vm.setTourCookie();
            },
          },
        });

        tour.addStep({
          text: vm.onboardingStepTwoText,
          beforeShowPromise() {
            return new Promise((resolve) => {
              vm.hideClassList();
              setTimeout(() => {
                window.scrollTo(0, 0);
                resolve();
              }, 500);
            });
          },
          attachTo: {
            element: this.isEmpty ? null : '.homeworks__tour',
            on: this.isEmpty ? null : 'left-start',
          },
          canClickTarget: false,
          modalOverlayOpeningRadius: 12,
          popperOptions: {
            placement: 'left-start',
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 50],
                },
              },
            ],
          },
          buttons: [
            {
              text: this.$t('onboarding.homeworks_step2.btn_rev'),
              secondary: true,
              action() {
                vm.prevOnboardingTag();
                return new Promise((resolve) => {
                  vm.showClassList();
                  setTimeout(() => {
                    this.back();
                    resolve();
                  }, 1000);
                });
              },
            },
            {
              text: this.$t('onboarding.homeworks_step2.btn'),
              action() {
                vm.acceptOnboardingTag();
                vm.$emit('input', !vm.value);
                vm.showClassList();
                vm.setTourCookie();
                return this.cancel();
              },
            },
          ],
          when: {
            show() {
              if (!vm.isEmpty) {
                vm.addArrow(this.tour, 'left');
              }
              vm.addProgressDots(this.tour);
            },
            cancel() {
              vm.closeOnboardingTag();
              vm.$emit('input', !vm.value);
              vm.showClassList();
              // vm.setTourCookie();
            },
          },
        });

        tour.start();

        Event.$on('cancelTour', () => {
          tour.cancel();
        });
      });
    },
  },

  beforeDestroy() {
    Event.$off('cancelTour');
  },

  watch: {
    value(value) {
      if (value) {
        this.startOnboarding();
      }
    },
  },
};
</script>
